import React, { useState, useEffect, useRef, useContext, forwardRef, useImperativeHandle } from 'react';
import styles from './InputBar.module.css';
import { IconButton, Tooltip } from '@mui/material';
import { toast } from 'react-hot-toast';
import { 
  IoAttach, 
  IoVolumeHigh,
  IoVolumeMute, 
  IoMic,
  IoSend,
  IoClose,
  IoLink,
  IoImage
} from 'react-icons/io5';
import { queryApi, codeGenApi, conversationsApi } from '../../services/api';
import { supabase } from '../../supabaseClient';
import SuggestedResponses from './SuggestedResponses';
import ImageUrlModal from './ImageUrlModal';
import FileUploadModal from './FileUploadModal';
import speechManager from '../../services/speechManager';
import historyManager from '../../services/historyManager';
import AttachmentModal from './AttachmentModal';
import cameraManager from '../../services/cameraManager';
import SpeechIndicator from './SpeechIndicator';
import { ScreenRecordingContext } from '../../contexts/ScreenRecordingContext';

const InputBar = forwardRef(({ 
  selectedPrompt, 
  inputRef, 
  micButtonRef, 
  activeConversationId, 
  activeCanvasId,
  currentConversation, 
  onProcessingChange,
  onCanvasSwitch,
  isSpeechEnabled,
  onSpeechToggle,
  onNewMessage,
  onUpdateConversationTitle,
  onConversationCreated,
  formSubmitRef 
}, ref) => {
  const [inputValue, setInputValue] = useState('');
  // For debounced localStorage persistence
  const inputDebounceTimeout = useRef(null);
  const LOCAL_STORAGE_KEY = 'inputBarText';
  const [isSpeechActive, setIsSpeechActive] = useState(false);
  const [suggestedResponses, setSuggestedResponses] = useState([]);
  const [lastCreatedCanvasId, setLastCreatedCanvasId] = useState(null);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const [processingType, setProcessingType] = useState(null);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const autoStartMic = localStorage.getItem('autoStartMic') === 'true';
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  
  // Image attachment states
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [activeAttachmentTab, setActiveAttachmentTab] = useState('url');
  const [attachedImageUrls, setAttachedImageUrls] = useState([]);
  
  // Keep track of the latest selected prompt
  const latestPromptRef = useRef(null);

  // Adjust textarea height when inputValue changes (for restored values)
  useEffect(() => {
    if (inputRef?.current) {
      const el = inputRef.current;
      el.style.height = 'auto';
      el.style.height = Math.min(120, el.scrollHeight) + 'px';
    }
  }, [inputValue, inputRef]);

  // Update tab title to conversation title
  useEffect(() => {
    if (currentConversation && currentConversation.title) {
      document.title = currentConversation.title;
    } else {
      document.title = 'Pralok';
    }
  }, [currentConversation]);

  // Log when activeConversationId changes
  useEffect(() => {
    console.log('Active conversation ID in InputBar:', activeConversationId);
    
    // Clear suggested responses when conversation changes
    setSuggestedResponses([]);
    
    // Clear attached images when conversation changes
    setAttachedImageUrls([]);
    
    // Reset input value when conversation changes
    let newValue = '';
    if (selectedPrompt) {
      newValue = selectedPrompt;
    } else {
      const persisted = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (persisted) {
        newValue = persisted;
      }
    }
    setInputValue(newValue);
    // Clear persisted input on conversation switch if we didn't restore it
    if (!newValue) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
    // Focus the input field when the conversation changes
    if (inputRef?.current) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        console.log('Focusing input field after conversation change');
        inputRef.current.focus();
      }, 100);
    }
  }, [activeConversationId, selectedPrompt]);

  // Handle selected prompt updates
  useEffect(() => {
    if (selectedPrompt) {
      setInputValue(selectedPrompt);
    }
  }, [selectedPrompt]);

  // Update latestPromptRef when selectedPrompt changes
  useEffect(() => {
    if (selectedPrompt) {
      latestPromptRef.current = selectedPrompt;
    }
  }, [selectedPrompt]);

  // Debounce localStorage update on inputValue change
  useEffect(() => {
    if (inputDebounceTimeout.current) {
      clearTimeout(inputDebounceTimeout.current);
    }
    inputDebounceTimeout.current = setTimeout(() => {
      // Only persist if not empty
      if (inputValue && inputValue.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_KEY, inputValue);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }, 500);
    return () => {
      if (inputDebounceTimeout.current) {
        clearTimeout(inputDebounceTimeout.current);
      }
    };
  }, [inputValue]);

  // Focus the input field when the component mounts
  useEffect(() => {
    // Focus the input field after a short delay to ensure the DOM is fully loaded
    const focusTimer = setTimeout(() => {
      if (inputRef?.current) {
        console.log('Auto-focusing input field');
        inputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(focusTimer);
  }, []); // Empty dependency array means this runs once on mount

  // Add speech status handler
  useEffect(() => {
    const handleSpeechStatus = (messageId, status) => {
      console.log('Speech status changed:', { messageId, status });
      
      // Update the speaking state based on the status
      if (status === 'speaking') {
        setIsAssistantSpeaking(true);
      } else if (status === 'completed' || status === 'paused') {
        setIsAssistantSpeaking(false);
        
        // Auto-start microphone if enabled and speech is completed
        if (status === 'completed' && autoStartMic) {
          setTimeout(() => {
            console.log('Auto-starting microphone after speech completion');
            toggleSpeechRecognition();
          }, 500);
        }
      }
    };

    // Set up the callbacks and ensure speechManager reflects our current state
    speechManager.setCallbacks(handleSpeechStatus);

    return () => {
      setIsAssistantSpeaking(false);
      speechManager.setCallbacks(null);
    };
  }, [autoStartMic]);

  // Reset isFirstMessage when conversation changes
  useEffect(() => {
    // Check if this is a new conversation (only has default title)
    setIsFirstMessage(currentConversation?.title === 'New Conversation');
  }, [currentConversation]);

  // Update historyManager when activeCanvasId changes
  useEffect(() => {
    if (activeCanvasId) {
      console.log('InputBar: Updating historyManager with activeCanvasId:', activeCanvasId);
      historyManager.setActiveCanvas(activeCanvasId);
    }
  }, [activeCanvasId]);

  // Function to send an automatic follow-up query without adding to chat history
  const sendAutomaticFollowUp = async (message, messageIds = null) => {
    try {
      console.log('Sending automatic follow-up query:', message);
      
      // Call handleSubmit with the automatic message, a flag to indicate it's automatic, and messageIds
      await handleSubmit(null, message, true, messageIds);
      
    } catch (error) {
      console.error('Error processing automatic follow-up query:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const processQueryResponse = async (response, isAutomaticQuery = false) => {
    // Track if canvasCreate or canvasEdit was processed
    let canvasWasCreated = false;
    let canvasWasEdited = false;
    let fetchMessagesWasProcessed = false;
    
    try {
      console.log('Query response:', response);
      const data = response.response;  // The response object contains a nested response
      
      if (data.suggestedResponses) {
        setSuggestedResponses(data.suggestedResponses);
      } else {
        setSuggestedResponses([]); // Clear suggestions when not present
      }
      
      // Always add assistant's response to chat history, even for automatic queries
      if (data.speak) {
        // Add assistant message to chat history with unique ID and mode
        const messageId = `msg-${Date.now()}`;
        onNewMessage?.({
          id: messageId,
          role: 'assistant',
          content: data.speak,  // Keep the original object with content and mode
          timestamp: Date.now(),
          isResponseToAutoQuery: isAutomaticQuery // Flag to indicate this is a response to an automatic query
        });
        
        // Handle text-to-speech if enabled
        if (isSpeechEnabled && data.speak && data.speak.content) {
          // Hide the processing spinner when TTS starts
          onProcessingChange(false);
          
          // Set speaking state right away
          setIsAssistantSpeaking(true);
          
          console.log('Starting speech for message:', messageId);
          // Speak the content without awaiting completion
          speechManager.speakText(
            data.speak.content,
            messageId
          ).catch(error => {
            console.error('Speech error:', error);
          });
          // Note: The speaking state will be reset by the speechManager via the onSpeechStatusChange callback
        }
      }
      // Process fetchMessages if present
      if (data.fetchMessages) {
        try {
          console.log('Processing fetchMessages:', data.fetchMessages);
          
          // fetchMessages can be a single ID or an array of IDs or ranges
          let messageIds = [];
          
          if (Array.isArray(data.fetchMessages)) {
            messageIds = data.fetchMessages;
          } else if (typeof data.fetchMessages === 'string') {
            // Try to parse if it's a string representation of an array
            try {
              if (data.fetchMessages.startsWith('[') && data.fetchMessages.endsWith(']')) {
                messageIds = JSON.parse(data.fetchMessages);
              } else {
                // Single ID as string
                messageIds = [parseInt(data.fetchMessages.replace('id-', ''), 10)];
              }
            } catch (e) {
              console.error('Error parsing fetchMessages:', e);
              messageIds = [data.fetchMessages]; // Use as is
            }
          } else if (typeof data.fetchMessages === 'number') {
            messageIds = [data.fetchMessages];
          }
          
          if (messageIds.length > 0) {
            console.log('Fetching messages with IDs:', messageIds);
            
            // Fetch the messages
            const fetchResponse = await queryApi.fetchMessages(activeConversationId, messageIds);
            
            if (fetchResponse.status === 'success' && fetchResponse.context) {
              // Store the context in historyManager
              historyManager.setFetchedMessagesContext(fetchResponse.context);
              
              // Send an automatic follow-up query to include the fetched messages
              await sendAutomaticFollowUp('Messages have been fetched', messageIds);
              
              // Set flag to indicate fetchMessages was processed
              fetchMessagesWasProcessed = true;
            }
          }
        } catch (error) {
          console.error('Error processing fetchMessages:', error);
          toast.error('Unable to fetch messages. Please try again.');
        }
      }
      if (data.canvasCreate) {
        try {
          onProcessingChange(true, 'canvas'); // Set canvas generation processing
          // Get user directly from Supabase
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            alert('Please log in to create a canvas');
            return;
          }
          // Generate code using the API service
          const generatedHtml = await codeGenApi.generateCode(data.canvasCreate);
          // Extract title from the HTML's title tag
          const titleMatch = generatedHtml.match(/<title>(.*?)<\/title>/);
          const title = titleMatch ? titleMatch[1] : 'New Canvas';
          // Create canvas using the API service
          const canvasData = await codeGenApi.createCanvas(
            user.id,
            activeConversationId,
            title,
            generatedHtml,
            'private',
            user.email,
            user.user_metadata?.full_name || user.user_metadata?.name,
            user.user_metadata?.avatar_url
          );
          // Store the newly created canvas ID
          setLastCreatedCanvasId(canvasData.id);
          console.log('Created new canvas with ID:', canvasData.id);
          
          // Update the historyManager with the new canvas ID and code
          historyManager.setLastCreatedCanvasId(canvasData.id);
          historyManager.setActiveCanvas(canvasData.id);
          historyManager.setGeneratedCanvasCode(generatedHtml);
          
          // Refresh conversation details to get updated canvas list
          const conversationResponse = await conversationsApi.getConversationDetails(
            activeConversationId,
            user.id
          );
          if (conversationResponse.status === 'success') {
            // For new conversations, add a small delay to ensure state updates complete
            const isNewConversation = conversationResponse.conversation.title === 'New Conversation';
            const delay = isNewConversation ? 50 : 0;
            
            setTimeout(() => {
              // Dispatch custom event to update App state
              window.dispatchEvent(new CustomEvent('conversationUpdated', { 
                detail: conversationResponse.conversation
              }));
              
              // For new conversations, explicitly switch to the new canvas
              if (isNewConversation && canvasData.id) {
                console.log('New conversation with new canvas, explicitly switching to canvas:', canvasData.id);
                const newCanvas = conversationResponse.conversation.canvas_details.find(c => c.id === canvasData.id);
                if (newCanvas) {
                  onCanvasSwitch?.(newCanvas);
                }
              }
            }, delay);
          }
          // Show success toast after canvas is created
          toast.success('New Canvas Created', {
            icon: '🎨'
          });
          
          // Set flag to indicate canvas was created
          canvasWasCreated = true;
        } catch (error) {
          console.error('Error creating canvas:', error);
          toast.error('Unable to create canvas. Please try again.');
        } finally {
          onProcessingChange(false);
        }
      }
      if (data.canvasEdit) {
        try {
          onProcessingChange(true, 'edit'); // Set edit processing
          // Get the canvas ID by removing 'id-' prefix
          const numericId = parseInt(data.canvasEdit.id.replace('id-', ''));
          if (isNaN(numericId)) {
            throw new Error('Invalid canvas ID format');
          }
          // Get the current canvas element to access its code
          const canvas = document.querySelector(`iframe[data-canvas-id="${data.canvasEdit.id}"]`);
          if (!canvas) {
            throw new Error('Canvas not found');
          }
          // Get current canvas code from the iframe's srcdoc
          const currentCode = canvas.srcdoc;
          // Send edit request to backend
          const editResponse = await codeGenApi.editCanvas(
            numericId,
            currentCode,
            data.canvasEdit.prompt
          );
          if (editResponse.content) {
            // Dispatch event to update canvas content
            window.dispatchEvent(new CustomEvent('canvasEdited', { 
              detail: {
                canvasId: data.canvasEdit.id,
                newCode: editResponse.content
              }
            }));
          }
          // Show success toast after canvas is edited
          toast.success('Canvas Edited', {
            icon: '✏️'
          });
          
          // Set flag to indicate canvas was edited
          canvasWasEdited = true;
        } catch (error) {
          console.error('Error editing canvas:', error);
          toast.error('Unable to edit canvas. Please try again.');
        } finally {
          onProcessingChange(false);
        }
      }
      if (data.adhocJS) {
        try {
          // Get the canvas ID from the adhocJS object
          const targetCanvasId = data.adhocJS.id?.replace('id-', '');
          
          // If no canvas ID provided, use the active canvas ID from props
          const canvasId = targetCanvasId || activeCanvasId;
          
          if (!canvasId) {
            throw new Error('No canvas ID found for adhocJS execution');
          }
          
          // Get the canvas element by its data-canvas-id attribute
          const canvas = document.querySelector(`iframe[data-canvas-id="id-${canvasId}"]`);
          if (!canvas) {
            throw new Error('Canvas element not found');
          }
          
          // Send the code to the iframe for execution
          canvas.contentWindow.postMessage({
            type: 'execute',
            code: data.adhocJS.code || data.adhocJS // Support both new and old formats
          }, '*');
          
          // Show info toast when Pralok interacts with canvas
          toast('Pralok interacted on canvas', {
            icon: '🤖',
            style: {
              background: '#2563eb',
              color: '#fff'
            }
          });
        } catch (error) {
          console.error('Error executing JS:', error);
          toast.error('Unable to process your request. Please try again.');
        }
      }
      if (data.switchCanvas) {
        try {
          // Get the numeric canvas ID by removing 'id-' prefix
          const numericId = parseInt(data.switchCanvas.replace('id-', ''));
          if (isNaN(numericId)) {
            throw new Error('Invalid canvas ID format');
          }
          // Find the canvas in the current conversation's canvas_details
          const targetCanvas = currentConversation?.canvas_details?.find(
            canvas => canvas.id === numericId && canvas.type !== 'separator'
          );
          if (!targetCanvas) {
            throw new Error('Target canvas not found');
          }
          // Use the onCanvasSwitch prop to navigate to the target canvas
          onCanvasSwitch(targetCanvas);
        } catch (error) {
          console.error('Error switching canvas:', error);
          alert('Failed to switch canvas: ' + error.message);
        }
      }
      if (data.fetchCanvasCode) {
        try {
          // Get the numeric canvas ID by removing 'id-' prefix
          const canvasId = data.fetchCanvasCode.replace('id-', '');
          if (!canvasId) {
            throw new Error('Invalid canvas ID format');
          }
          
          // Fetch the canvas code from the API
          const canvasData = await codeGenApi.getCanvasCode(canvasId);
          
          if (!canvasData || canvasData.status !== 'success') {
            throw new Error('Failed to fetch canvas code');
          }
          
          // Send the canvas code back to the AI in a follow-up message
          const canvasCodeMessage = `Canvas code for ${data.fetchCanvasCode}:\n\`\`\`html\n${canvasData.code}\n\`\`\``;
          sendAutomaticFollowUp(canvasCodeMessage);
          
          // Show success toast
          toast.success('Canvas code retrieved', {
            icon: '📄'
          });
        } catch (error) {
          console.error('Error fetching canvas code:', error);
          toast.error('Unable to load canvas. Please try again.');
          
          // Send error message back to AI
          sendAutomaticFollowUp(`Error fetching canvas code: ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Error processing response:', error);
      toast.error('Something went wrong. Please try again.');
      setIsAssistantSpeaking(false);
      onProcessingChange(false);
    } finally {
      // After all tools have been processed, check if we need to send a follow-up query
      // Only send follow-up query if this is not already an automatic query
      if (!isAutomaticQuery) {
        if (canvasWasCreated) {
          // Small delay to ensure UI updates are complete
          setTimeout(() => {
            sendAutomaticFollowUp('Canvas has been created');
          }, 500);
        } else if (canvasWasEdited) {
          // Small delay to ensure UI updates are complete
          setTimeout(() => {
            sendAutomaticFollowUp('Canvas has been edited');
          }, 500);
        }
      }
    }
  };

  const handleSuggestedResponseSelect = (response) => {
    setInputValue(response);
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  };

  const createNewConversation = async (initialTitle = 'New Conversation') => {
    try {
      setIsCreatingConversation(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to start a conversation', {
          duration: 3000,
          icon: '⚠️'
        });
        return null;
      }

      const response = await conversationsApi.createConversation(
        user.id,
        initialTitle,
        user.email,
        user.user_metadata?.full_name || user.user_metadata?.name,
        user.user_metadata?.avatar_url
      );
      
      if (response.status === 'success') {
        const conversationResponse = await conversationsApi.getConversationDetails(response.id, user.id);
        if (conversationResponse.status === 'success') {
          window.dispatchEvent(new CustomEvent('conversationUpdated', { 
            detail: conversationResponse.conversation
          }));
          return response.id;
        }
      }
      return null;
    } catch (err) {
      console.error('Error creating new conversation:', err);
      toast.error('Unable to start a new conversation. Please try again.');
      return null;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const { recordedImages, clearRecordedImages } = useContext(ScreenRecordingContext);

  const handleSubmit = async (e, automaticMessage = null, isAutomaticQuery = false, messageIds = null) => {
    // Clear persisted input on submit
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    e?.preventDefault();
    
    let wasNewConversationCreated = false; // Flag to track if we created a convo in this run

    // Use either the automatic message or the input value
    const query = automaticMessage || inputValue.trim();
    // Store the raw text value for passing to the API
    const rawText = query;

    // Skip validation for automatic queries
    if (!isAutomaticQuery) {
      // Verify that we're submitting the latest prompt
      if (latestPromptRef.current && query !== latestPromptRef.current) {
        console.log('Preventing submission of stale prompt');
        return;
      }
    }
    
    if (!query && !attachedImageUrls.length && !recordedImages.length) return;

    // Reset states (only for non-automatic queries) immediately after validation
    if (!isAutomaticQuery) {
      setInputValue('');
      setAttachedImageUrls([]);
      clearRecordedImages();
    }

    try {
      // First, create new conversation if needed
      if (!activeConversationId) {
        // Use the first 50 characters of the message as the title
        const newTitle = query.length > 50 ? query.substring(0, 47) + '...' : query;
        
        onProcessingChange(true, 'conversation');
        
        const newConversationId = await createNewConversation(newTitle);
        if (!newConversationId) {
          toast.error('Unable to start a new conversation. Please try again.');
          onProcessingChange(false);
          return;
        }
        activeConversationId = newConversationId;
        wasNewConversationCreated = true; // Set the flag here
        // Notify parent about the newly created conversation
        if (onConversationCreated) {
          onConversationCreated(newConversationId);
        } 
      }

      // Structure images with source information
      const structuredImages = isAutomaticQuery ? [] : [
        ...attachedImageUrls.map(url => ({ url, source: 'attached' })),
        ...recordedImages.map(url => ({ url, source: 'screen_record' }))
      ];

      // Add the user message to chat history (only for non-automatic queries)
      if (!isAutomaticQuery) {
        // Use the URLs from the structured data for display
        const displayImageUrls = structuredImages.map(img => img.url);
        const userMessage = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: query,
          timestamp: Date.now(),
          images: displayImageUrls.length > 0 ? displayImageUrls : undefined
        };
        onNewMessage?.(userMessage);
      } else {
        console.log('Automatic query, not adding to chat history:', query);
        // For debugging purposes, we can still add the message but mark it as automatic
        // This will be filtered out in the ChatHistory component
        const autoMessage = {
          id: `auto-user-${Date.now()}`,
          role: 'user',
          content: query,
          timestamp: Date.now(),
          isAutomatic: true // Flag to indicate this is an automatic message
        };
        onNewMessage?.(autoMessage);
      }

      // Update spinner to show query processing
      onProcessingChange(true, 'query');

      // Ensure active canvas ID is set before constructing query
      if (activeCanvasId) {
        historyManager.setActiveCanvas(activeCanvasId);
      }

      // Process the query
      const completeQuery = historyManager.constructQueryMessage(query);
      const response = await queryApi.processQuery(
        completeQuery,
        activeConversationId,
        historyManager.eventHistory,
        structuredImages, // Send the structured list
        historyManager.activeCanvasId ? [historyManager.activeCanvasId] : [],
        rawText, // Pass the raw text to the API
        isAutomaticQuery, // Pass the isAutomaticQuery flag
        messageIds // Pass the messageIds
      );
      
      // Use the flag we set earlier instead of relying on props
      if (wasNewConversationCreated && activeConversationId) {
        console.log(`Attempting to auto-name new conversation: ${activeConversationId}`);
        // Don't block the UI, run this in the background
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user.id;
        conversationsApi.autonameConversation(activeConversationId, userId)
          .then(autonameResponse => {
            if (autonameResponse.status === 'success' && autonameResponse.title) {
              console.log(`Successfully auto-named conversation ${activeConversationId} to: ${autonameResponse.title}`);
              // Use the prop function to update the title in the parent state/UI
              if (onUpdateConversationTitle) {
                onUpdateConversationTitle(activeConversationId, autonameResponse.title);
              }
            } else {
              console.warn(`Auto-naming failed or returned unexpected data for ${activeConversationId}:`, autonameResponse);
            }
          })
          .catch(error => {
            console.error(`Error auto-naming conversation ${activeConversationId}:`, error);
            // Optional: Add user feedback here if needed
          });
      }
      
      // Input has already been cleared, just continue processing
      if (!isAutomaticQuery && inputRef?.current) {
        inputRef.current.style.height = 'auto';
      }
      
      // Process the response
      await processQueryResponse(response, isAutomaticQuery);
      
    } catch (error) {
      console.error('Error processing query:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      onProcessingChange(false);
    }
  };

  // Expose submit function through ref
  useEffect(() => {
    if (formSubmitRef) {
      formSubmitRef.current = () => {
        const form = document.querySelector('form');
        if (form) {
          // Only submit if the input value matches the latest prompt
          if (latestPromptRef.current && inputValue.trim() !== latestPromptRef.current) {
            console.log('Preventing form submission with stale prompt');
            return;
          }
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      };
    }
  }, [formSubmitRef, inputValue]);

  const initializeSpeechRecognition = () => {
    // Only initialize when it's actually used, not on component mount
    if (!recognitionRef.current && 'webkitSpeechRecognition' in window) {
      console.log('Initializing speech recognition');
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscriptRef.current += ' ' + transcript;
          }
        }
        // Reset timeout for stopping recognition
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        }, 2000);
      };
      
      recognitionRef.current.onend = () => {
        console.log('Speech recognition ended');
        // Only append the final transcript when recognition ends
        if (finalTranscriptRef.current) {
          setInputValue(prev => prev + finalTranscriptRef.current.trim());
          finalTranscriptRef.current = ''; // Reset for next recording
        }
        setIsSpeechActive(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsSpeechActive(false);
        finalTranscriptRef.current = ''; // Reset on error
      };

      // Add onstart handler to track when recognition actually starts
      recognitionRef.current.onstart = () => {
        console.log('Speech recognition started');
        setIsSpeechActive(true);
      };
    }
  };

  // Don't initialize speech recognition on component mount
  useEffect(() => {
    // Only setup cleanup
    return () => {
      console.log('InputBar unmounting - cleaning up speech recognition');
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const toggleSpeechRecognition = () => {
    // Lazily initialize speech recognition only when the user clicks the button
    if (!recognitionRef.current) {
      initializeSpeechRecognition();
    }
    
    if (recognitionRef.current) {
      if (!isSpeechActive) {
        console.log('Starting speech recognition');
        finalTranscriptRef.current = ''; // Reset transcript before starting
        try {
          recognitionRef.current.start();
          // Note: We don't set isSpeechActive here, we'll let the onstart handler do it
        } catch (error) {
          console.error('Error starting speech recognition:', error);
        }
      } else {
        console.log('Stopping speech recognition');
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        try {
          recognitionRef.current.stop();
          setIsSpeechActive(false);
        } catch (error) {
          console.error('Error stopping speech recognition:', error);
        }
      }
    } else {
      console.error('Speech recognition not supported in this browser');
    }
  };

  const handleSpeechButtonClick = () => {
    const newState = !isSpeechEnabled;
    onSpeechToggle?.(newState);
  };

  // Open the attachment modal
  const handleOpenAttachmentModal = () => {
    setIsAttachmentModalOpen(true);
  };
  
  // Cleanup function for attachment modal
  const handleCloseAttachmentModal = () => {
    console.log("InputBar: closing attachment modal - stopping all cameras");
    // Force stop all cameras
    cameraManager.stopAllCameras();
    
    // First switch to a non-camera tab to ensure camera cleanup
    if (activeAttachmentTab === 'camera') {
      setActiveAttachmentTab('url');
    }
    
    // Then close the modal
    setIsAttachmentModalOpen(false);
  };

  // Cleanup effect for unmounting
  useEffect(() => {
    return () => {
      console.log("InputBar unmounting - stopping all cameras");
      // Ensure camera is cleaned up when component unmounts
      cameraManager.stopAllCameras();
    };
  }, []);

  // Handle image URLs from modal
  const handleImageUrlSubmit = (urls) => {
    if (urls && urls.length > 0) {
      setAttachedImageUrls([...attachedImageUrls, ...urls]);
      setIsAttachmentModalOpen(false);
      toast.success(`Added ${urls.length} image URL${urls.length > 1 ? 's' : ''}`, {
        icon: '🖼️'
      });
    }
  };
  
  // Handle file upload results
  const handleFileUploadSubmit = (base64Images) => {
    if (base64Images && base64Images.length > 0) {
      setAttachedImageUrls([...attachedImageUrls, ...base64Images]);
      setIsAttachmentModalOpen(false);
      toast.success(`Added ${base64Images.length} image${base64Images.length > 1 ? 's' : ''}`, {
        icon: '📷'
      });
    }
  };

  // Handle camera capture results
  const handleCameraSubmit = (capturedImages) => {
    if (capturedImages && capturedImages.length > 0) {
      setAttachedImageUrls([...attachedImageUrls, ...capturedImages]);
      setIsAttachmentModalOpen(false);
      toast.success('Added captured image', {
        icon: '📸'
      });
    }
  };

  // Handle screen capture results
  const handleScreenSubmit = (screenshots) => {
    if (screenshots && screenshots.length > 0) {
      setAttachedImageUrls([...attachedImageUrls, ...screenshots]);
      setIsAttachmentModalOpen(false);
      toast.success(`Added ${screenshots.length} screenshot${screenshots.length > 1 ? 's' : ''}`, {
        icon: '🖥️'
      });
    }
  };

  // Handle removing an image
  const handleRemoveImage = (index) => {
    setAttachedImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    // Poll for speech status every 100ms
    const interval = setInterval(() => {
      setIsAssistantSpeaking(speechManager.isSpeaking());
    }, 100);

    return () => {
      clearInterval(interval);
      setIsAssistantSpeaking(false);
    };
  }, []);

  // Remove the speech status handler since we're using polling now
  useEffect(() => {
    const handleSpeechStatus = (messageId, status) => {
      if (status === 'completed' && autoStartMic) {
        setTimeout(() => {
          toggleSpeechRecognition();
        }, 500);
      }
    };

    speechManager.setCallbacks(handleSpeechStatus);

    return () => {
      speechManager.setCallbacks(null);
    };
  }, [autoStartMic]);

  // Expose sendAutomaticFollowUp via ref
  useImperativeHandle(ref, () => ({
    sendAutomaticFollowUp: (message, messageIds = null) => {
      // Ensure we have an active conversation ID before sending
      if (!activeConversationId) {
        console.warn("Attempted to send automatic follow-up without an active conversation ID.");
        toast.error("Cannot send message: No active conversation.");
        return;
      }
      sendAutomaticFollowUp(message, messageIds);
    }
  }));

  return (
    <div className={styles.inputBarContainer}>
      <SuggestedResponses 
        responses={suggestedResponses}
        onSelect={handleSuggestedResponseSelect}
      />
      
      <div className={styles.inputContainer}>
        <form className={styles.inputWrapper} onSubmit={(e) => handleSubmit(e)}>
          <SpeechIndicator isAssistantSpeaking={isAssistantSpeaking} />
          <textarea
            className={styles.input}
            placeholder="Let us discuss about..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            rows="1"
            ref={inputRef}
            onInput={(e) => {
              // Auto-resize the textarea but keep it at the bottom
              // Set height to auto first to shrink if needed
              e.target.style.height = 'auto';
              
              // Set the new height based on content but with a maximum
              e.target.style.height = Math.min(120, e.target.scrollHeight) + 'px';
              
              // No need for transforms - align-items: flex-end in the parent will
              // keep the content aligned at the bottom naturally
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />
          
          <div className={styles.buttonGroup}>
            {processingType ? (
              <div className={styles.processingIndicator}>
                <div className={styles.spinner} />
                <span className={styles.processingText}>
                  {processingType === 'canvas' ? 'Generating Canvas...' :
                  processingType === 'edit' ? 'Editing Canvas...' :
                  processingType === 'conversation' ? 'Creating Conversation...' :
                  'Processing...'}
                </span>
              </div>
            ) : (
              <>
                <Tooltip title="Add attachment" placement="top">
                  <IconButton 
                    className={`${styles.actionButton} ${attachedImageUrls.length > 0 ? styles.hasAttachments : ''}`}
                    aria-label="Add attachment"
                    onClick={handleOpenAttachmentModal}
                  >
                    <IoAttach size={18} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={`Text to speech ${isSpeechEnabled ? 'on' : 'off'}`} placement="top">
                  <IconButton 
                    className={`${styles.actionButton} ${isSpeechEnabled ? styles.active : ''}`}
                    aria-label="Text to speech"
                    onClick={handleSpeechButtonClick}
                  >
                    {isSpeechEnabled ? <IoVolumeHigh size={18} /> : <IoVolumeMute size={18} />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Voice input" placement="top">
                  <IconButton 
                    className={`${styles.actionButton} ${isSpeechActive ? styles.sendButton : ''}`}
                    aria-label="Voice input"
                    ref={micButtonRef}
                    onClick={toggleSpeechRecognition}
                  >
                    <IoMic size={18} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Send message" placement="top">
                  <IconButton 
                    className={`${styles.actionButton} ${styles.sendButton}`}
                    type="submit"
                    aria-label="Send message"
                    disabled={!inputValue.trim() && attachedImageUrls.length === 0}
                  >
                    <IoSend size={18} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        </form>
      </div>
      
      {/* Portal-based Attachment Modal */}
      <AttachmentModal 
        isOpen={isAttachmentModalOpen}
        onClose={handleCloseAttachmentModal}
        activeTab={activeAttachmentTab}
        onTabChange={setActiveAttachmentTab}
        onImageUrlSubmit={handleImageUrlSubmit}
        onFileUploadSubmit={handleFileUploadSubmit}
        onCameraSubmit={handleCameraSubmit}
        onScreenSubmit={handleScreenSubmit}
      />
    </div>
  );
});

export default InputBar;
